import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type MouseEvent,
  type MutableRefObject,
  type PointerEvent,
  type SetStateAction,
} from "react";

import { cn } from "@/lib/utils";
import { isBetaFeatureEnabled } from "./feature-flag";

const BETA_FEATURE_INSPECTOR_LOCAL_STORAGE_KEY = "beta-feature-inspector";
const BETA_FEATURE_INSPECTOR_POSITION_LOCAL_STORAGE_KEY =
  "beta-feature-inspector-position";
const BETA_FEATURE_INSPECTOR_BUTTON_WIDTH = 88;
const BETA_FEATURE_INSPECTOR_BUTTON_HEIGHT = 32;
const BETA_FEATURE_INSPECTOR_MARGIN = 16;
const BETA_FEATURE_INSPECTOR_DRAG_THRESHOLD = 3;

type BetaFeatureInspectorPosition = {
  left: number;
  top: number;
};

type BetaFeatureInspectorDragState = {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startLeft: number;
  startTop: number;
  hasDragged: boolean;
};

const readBetaFeatureInspectorEnabled = (): boolean =>
  typeof localStorage !== "undefined" &&
  localStorage.getItem(BETA_FEATURE_INSPECTOR_LOCAL_STORAGE_KEY) === "true";

const areSamePosition = (
  a: BetaFeatureInspectorPosition,
  b: BetaFeatureInspectorPosition,
): boolean => a.left === b.left && a.top === b.top;

const clampBetaFeatureInspectorPosition = ({
  left,
  top,
}: BetaFeatureInspectorPosition): BetaFeatureInspectorPosition => {
  if (typeof window === "undefined") return { left, top };

  const maxLeft = Math.max(
    BETA_FEATURE_INSPECTOR_MARGIN,
    window.innerWidth -
      BETA_FEATURE_INSPECTOR_BUTTON_WIDTH -
      BETA_FEATURE_INSPECTOR_MARGIN,
  );
  const maxTop = Math.max(
    BETA_FEATURE_INSPECTOR_MARGIN,
    window.innerHeight -
      BETA_FEATURE_INSPECTOR_BUTTON_HEIGHT -
      BETA_FEATURE_INSPECTOR_MARGIN,
  );

  return {
    left: Math.min(Math.max(left, BETA_FEATURE_INSPECTOR_MARGIN), maxLeft),
    top: Math.min(Math.max(top, BETA_FEATURE_INSPECTOR_MARGIN), maxTop),
  };
};

const getDefaultBetaFeatureInspectorPosition =
  (): BetaFeatureInspectorPosition => {
    if (typeof window === "undefined") {
      return {
        left: BETA_FEATURE_INSPECTOR_MARGIN,
        top: BETA_FEATURE_INSPECTOR_MARGIN,
      };
    }

    return {
      left:
        window.innerWidth -
        BETA_FEATURE_INSPECTOR_BUTTON_WIDTH -
        BETA_FEATURE_INSPECTOR_MARGIN,
      top:
        window.innerHeight -
        BETA_FEATURE_INSPECTOR_BUTTON_HEIGHT -
        BETA_FEATURE_INSPECTOR_MARGIN,
    };
  };

const readBetaFeatureInspectorPosition = (): BetaFeatureInspectorPosition => {
  if (typeof localStorage === "undefined") {
    return getDefaultBetaFeatureInspectorPosition();
  }

  const raw = localStorage.getItem(
    BETA_FEATURE_INSPECTOR_POSITION_LOCAL_STORAGE_KEY,
  );
  if (!raw) return getDefaultBetaFeatureInspectorPosition();

  try {
    const parsed = JSON.parse(raw) as Partial<BetaFeatureInspectorPosition>;
    if (typeof parsed.left !== "number" || typeof parsed.top !== "number") {
      return getDefaultBetaFeatureInspectorPosition();
    }
    return clampBetaFeatureInspectorPosition({
      left: parsed.left,
      top: parsed.top,
    });
  } catch {
    return getDefaultBetaFeatureInspectorPosition();
  }
};

const setBodyBetaFeatureInspector = (enabled: boolean) => {
  if (typeof document === "undefined") return;
  document.body.classList.toggle("beta-feature-inspector", enabled);
};

const writeBetaFeatureInspectorEnabled = (enabled: boolean) => {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(
    BETA_FEATURE_INSPECTOR_LOCAL_STORAGE_KEY,
    String(enabled),
  );
};

const writeBetaFeatureInspectorPosition = (
  position: BetaFeatureInspectorPosition,
) => {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(
    BETA_FEATURE_INSPECTOR_POSITION_LOCAL_STORAGE_KEY,
    JSON.stringify(position),
  );
};

const useBetaFeatureInspectorBodyClass = (inspectorEnabled: boolean) => {
  useEffect(() => {
    setBodyBetaFeatureInspector(inspectorEnabled);
    writeBetaFeatureInspectorEnabled(inspectorEnabled);

    return () => {
      setBodyBetaFeatureInspector(false);
    };
  }, [inspectorEnabled]);
};

const useStoredBetaFeatureInspectorPosition = () => {
  const [position, setPosition] = useState(readBetaFeatureInspectorPosition);
  const positionRef = useRef(position);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => {
        const next = clampBetaFeatureInspectorPosition(prev);
        if (areSamePosition(prev, next)) {
          return prev;
        }

        positionRef.current = next;
        writeBetaFeatureInspectorPosition(next);
        return next;
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return { position, positionRef, setPosition };
};

const useBetaFeatureInspectorDrag = ({
  position,
  positionRef,
  setPosition,
}: {
  position: BetaFeatureInspectorPosition;
  positionRef: MutableRefObject<BetaFeatureInspectorPosition>;
  setPosition: Dispatch<SetStateAction<BetaFeatureInspectorPosition>>;
}) => {
  const dragStateRef = useRef<BetaFeatureInspectorDragState | null>(null);
  const shouldSuppressClickRef = useRef(false);

  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    dragStateRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startLeft: position.left,
      startTop: position.top,
      hasDragged: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - dragState.startClientX;
    const deltaY = event.clientY - dragState.startClientY;
    if (
      !dragState.hasDragged &&
      Math.hypot(deltaX, deltaY) < BETA_FEATURE_INSPECTOR_DRAG_THRESHOLD
    ) {
      return;
    }

    dragState.hasDragged = true;
    const next = clampBetaFeatureInspectorPosition({
      left: dragState.startLeft + deltaX,
      top: dragState.startTop + deltaY,
    });
    positionRef.current = next;
    setPosition(next);
  };

  const finishDrag = (event: PointerEvent<HTMLButtonElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (dragState.hasDragged) {
      writeBetaFeatureInspectorPosition(positionRef.current);
    }
    shouldSuppressClickRef.current = dragState.hasDragged;
    if (dragState.hasDragged) {
      window.setTimeout(() => {
        shouldSuppressClickRef.current = false;
      }, 0);
    }
    dragStateRef.current = null;
  };

  const handlePointerUp = (event: PointerEvent<HTMLButtonElement>) => {
    finishDrag(event);
  };

  const handlePointerCancel = (event: PointerEvent<HTMLButtonElement>) => {
    finishDrag(event);
    shouldSuppressClickRef.current = false;
    dragStateRef.current = null;
  };

  const handleClick = (
    event: MouseEvent<HTMLButtonElement>,
    onToggle: () => void,
  ) => {
    if (shouldSuppressClickRef.current) {
      shouldSuppressClickRef.current = false;
      event.preventDefault();
      return;
    }
    onToggle();
  };

  return {
    handleClick,
    handlePointerCancel,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
  };
};

const BetaFeatureInspectorButton = () => {
  const [inspectorEnabled, setInspectorEnabled] = useState(
    readBetaFeatureInspectorEnabled,
  );
  const { position, positionRef, setPosition } =
    useStoredBetaFeatureInspectorPosition();
  const {
    handleClick,
    handlePointerCancel,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
  } = useBetaFeatureInspectorDrag({ position, positionRef, setPosition });

  useBetaFeatureInspectorBodyClass(inspectorEnabled);

  return (
    <button
      type="button"
      aria-pressed={inspectorEnabled}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onClick={(event) =>
        handleClick(event, () => setInspectorEnabled((prev) => !prev))
      }
      style={{ left: position.left, top: position.top }}
      className={cn(
        "fixed z-[9999] h-8 rounded-[6px] border px-3 label-2-medium shadow-lg transition-colors cursor-grab active:cursor-grabbing touch-none select-none",
        inspectorEnabled
          ? "border-red-500 bg-red-500 text-static-white"
          : "border-line-normal bg-background-base text-label-normal",
      )}
    >
      Beta 표시
    </button>
  );
};

export const BetaFeatureInspectorToggle = () => {
  if (!isBetaFeatureEnabled()) return null;

  return <BetaFeatureInspectorButton />;
};
